import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { TabBar } from "src/components/tabbar";
import { TabSection } from "src/pages/dealRoom/pages/assets";
import IntegrationCard, { IntegrationStatus } from "../components/integrationCard";
import IntegrationsOptionsDropdown from "../components/dropdown";
import { useIntegrationsQuery } from "src/query/settings/integrations";
import { useCapabilitiesFetch } from "src/query/settings/dashboard";
import { getIntegrationPath } from "src/utils/util";
import withErrorBoundary from "src/components/errorBoundary/withErrorBoundary";

import _ from "lodash";
import cx from "classnames";
import styles from "./styles.module.scss";
import EmptyMessage from "src/components/emptyMessage";
import SlidePaneView from "src/legacy/components/SlidePaneView";
import IntegrationValidateView from "src/legacy/pages/settings/integrations/integrations/IntegrationConfirmView";
import userRoleService from "src/utils/userRoles.service";
import { useSelector } from "react-redux";
import { shouldFetchIntegrations } from "src/legacy/services/utils";
import { useDispatch } from "react-redux";
import { fetchIntegrations } from "src/legacy/redux/actions/integrations";

function Integrations() {
  const dispatch : any = useDispatch();
  const navigate = useNavigate();

  const legacyIntegrationsData = useSelector((state) =>  _.get(state,"legacyRootReducer.settings.integrations",[]));

  const [activeTab, setActiveTab] = React.useState(0);
  const [selectedOption, setSelectedOption] = React.useState<
    { value: string; label: string } | null
  >({ value: "all", label: "All Integrations" });

  const { data: integrations, isLoading: isLoadingIntegrations } = useIntegrationsQuery();
  const { data: techStack, isLoading: isLoadingAdminIntegrations } = useCapabilitiesFetch();

  const activeWorkspace = useSelector((state) => _.get(state, "cleverStory.workspaces",[]));
  const isCleverstoryIntegrated = _.size(activeWorkspace) > 0;

  const [showIntegrationConfirmView, setShowIntegrationConfirmView] = React.useState(false);
  const [integrationClickState, setIntegrationClickState] = React.useState({
    activatedIntegration: null,
    clickedIntegration: null,
  });

  const isAdmin = userRoleService.isAdmin();

  const adminIntegrations = techStack?.filter(
    (item) => item.productCapabilityResponse?.integration,
  ) || [];

  useEffect(() => {
    if (shouldFetchIntegrations(legacyIntegrationsData)) {
      dispatch(fetchIntegrations());
    }
  }, [])

  const activatedAdminIntegrations = useMemo(() => {
    if (!integrations || !adminIntegrations) return [];
    return adminIntegrations
      .map((item) => {
        const type = item.productCapabilityResponse?.type;
        const matchingIntegration =
          integrations.find((integration) => integration.key === type) || null;
        const activatedIntegration = matchingIntegration?.state?.code === "ok";
        const isMatchingCategory =
          selectedOption?.value === "all" ||
          item.productCapabilityResponse?.category?.id ===
          selectedOption?.value;

        var isActivated = false;
        var enabled = item.enabled
        if(type === "cleverstory_integration"){
          isActivated = isCleverstoryIntegrated;
          enabled = isCleverstoryIntegrated ? true : item.enabled;
        }
        else{
          isActivated =  matchingIntegration
            ? item.enabled && activatedIntegration
            : false
        }

        return {
          ...item,
          enabled: enabled,
          // if the matching integration is there and it is selected and it is active
          activated: isActivated,
          // if the matching integration is there and it is selected by admin and it is not active
          deactivated: !matchingIntegration
            ? item.enabled && !activatedIntegration
            : false,
          // if matching integration is there and it is selected and it has an auth error
          requiredAction:
            matchingIntegration?.state?.code === "auth_error" && item.enabled,
          isMatchingCategory,
          adminLevel: item.productCapabilityResponse?.category?.type === "ACCOUNT_SPECIFIC",
        };
      })
      .filter((integration) => integration.isMatchingCategory && integration.enabled);
  }, [integrations, adminIntegrations, selectedOption, isCleverstoryIntegrated]);

  // integrations that are not selected on an admin level but are active for users
  const additionalActiveIntegrations = useMemo(() => {
    if (!integrations || !adminIntegrations) return [];

    const existingKeys = activatedAdminIntegrations.map(
      (item) => item.productCapabilityResponse?.type,
    );

    const additionalIntegrations = integrations
      .filter((integration) => {
        return !existingKeys.includes(integration.key);
      })
      .map((integration) => {
        const activatedIntegration = integration.state?.code === "ok";
        const isMatchingCategory =
          selectedOption?.value === "all" ||
          integration.category?.id === selectedOption?.value;

        const productCapabilityResponse = adminIntegrations.find(
          (item) => item.productCapabilityResponse?.type === integration.key,
        )?.productCapabilityResponse;

        return {
          productCapabilityResponse: {
            ...productCapabilityResponse,
          },
          enabled: true,
          activated: activatedIntegration,
          deactivated:
            !activatedIntegration && integration.state?.code !== "auth_error",
          requiredAction: integration.state?.code === "auth_error",
          isMatchingCategory,
          adminLevel: productCapabilityResponse?.category?.type === "ACCOUNT_SPECIFIC"
        };
      })
      .filter((integration) => integration.isMatchingCategory);

    return additionalIntegrations;
  }, [
    integrations,
    adminIntegrations,
    activatedAdminIntegrations,
    selectedOption,
  ]);

 const allActivatedIntegrations = useMemo(() => {

    const filteredAdditionalIntegrations = activatedAdminIntegrations.filter(integration => {
      return integration.adminLevel ? (isAdmin ? integration : (integration.activated)): integration;
    });

    return [
      ...filteredAdditionalIntegrations,
      ...additionalActiveIntegrations,
    ];
  }, [activatedAdminIntegrations, additionalActiveIntegrations, isAdmin]);

  const filteredIntegrations = useMemo(() => {
    return allActivatedIntegrations.filter(integration => {
      if (activeTab === 0) return true;
      if (activeTab === 1) return integration.activated && !integration.requiredAction;
      if (activeTab === 2) return integration.deactivated;
      if (activeTab === 3) return integration.requiredAction;
      return false;
    });
  }, [allActivatedIntegrations, activeTab]);

  const excludedCategory = ["CRM", "ANALYTICS"]

  const handleIntegrationClick = (integration) => {
    const category = _.get(integration, "productCapabilityResponse.category.name", "");
    let existingIntegration = getIntegrationForCategory(category);
    if ((existingIntegration != null && !integration.activated && !excludedCategory.includes(category) && (existingIntegration.key != integration.productCapabilityResponse.type))) {
      setIntegrationClickState({
        activatedIntegration: existingIntegration,
        clickedIntegration: integration,
      });
      setShowIntegrationConfirmView(true);
    } else {
      const integrationType = integration.productCapabilityResponse?.type;
      if (integrationType) {
        const getPath = getIntegrationPath(integrationType);
        if (getPath) {
          navigate(`/settings/integrations/${getPath.toLowerCase()}`);
        }
      }
    }
  }

  const getIntegrationForCategory = (category) => {
    try {
      let integration = _.filter(integrations, item => {
        return item != null
      })
        .find((item) => {
          return item.category.name == category
        })

      return integration;
    } catch (e) { }

    return null;
  }


  if (isLoadingIntegrations || isLoadingAdminIntegrations) {
    return <div className={styles.loadingMessage}>Loading...</div>;
  }

  const renderIntegrationCards = (integrations) => (
    _.size(integrations) > 0 ? integrations.map((integration, index) => (
      integration.productCapabilityResponse.integration &&
      <IntegrationCard
        cursor="pointer"
        handleClick={() => handleIntegrationClick(integration)}
        key={`${integration.productCapabilityResponse.type}-${index}`}
        title={integration.productCapabilityResponse.name}
        subTitle={integration.productCapabilityResponse.description}
        iconUrl={integration.productCapabilityResponse.icon}
        type="integration"
        actionType={<IntegrationStatus handleClick={() => handleIntegrationClick(integration)} enabled={integration.activated} reauthenticate={integration.requiredAction} />}
      />
    )) : <EmptyMessage
      width="100%"
      image={'/images/kiteErrorMessage.svg'}
      heading="No integrations found!"
    />
  );

  return (
    <div className={cx(styles.container)}>
      <div className={styles.header}>
        <div className={styles.title}>Integrations</div>
        <div className={styles.subText}>
          Connect your favorite tools to Paperflite
        </div>
      </div>

      <div className={styles.sectionContainer}>
        <div className={styles.tabContainer}>
          <TabBar
            className={cx(styles.tabs)}
            gap={32}
            tabSize="equal"
            active={activeTab}
            onSelectionChange={(tab) => {
              setActiveTab(tab);
            }}
          >
            <div className={cx(styles.tab, { [styles.active]: activeTab === 0 })}>
              All
            </div>
            <div className={cx(styles.tab, { [styles.active]: activeTab === 1 })}>
              Active
            </div>
            <div className={cx(styles.tab, { [styles.active]: activeTab === 2 })}>
              Deactivated
            </div>
            <div className={cx(styles.tab, { [styles.active]: activeTab === 3 })}>
              Action needed
            </div>
          </TabBar>
          <IntegrationsOptionsDropdown
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
          />
        </div>
        <div className={cx(styles.stacks, styles.cardsContainer)}>
          <div className={styles.tabBody}>
            <TabSection index={0} selected={activeTab}>
              <div className={styles.allIntegrations}>
                {renderIntegrationCards(allActivatedIntegrations)}
              </div>
            </TabSection>
            <TabSection index={1} selected={activeTab}>
              <div className={styles.allIntegrations}>
                {renderIntegrationCards(filteredIntegrations)}
              </div>
            </TabSection>
            <TabSection index={2} selected={activeTab}>
              <div className={styles.allIntegrations}>
                {renderIntegrationCards(filteredIntegrations)}
              </div>
            </TabSection>
            <TabSection index={3} selected={activeTab}>
              <div className={styles.allIntegrations}>
                {renderIntegrationCards(filteredIntegrations)}
              </div>
            </TabSection>
          </div>
        </div>
      </div>

      {showIntegrationConfirmView && <SlidePaneView width="30vw" show={showIntegrationConfirmView}>
        <IntegrationValidateView activatedIntegration={integrationClickState.activatedIntegration}
          clickedIntegration={integrationClickState.clickedIntegration}
          onCancel={
            () => {
              setShowIntegrationConfirmView(false);
              setIntegrationClickState({
                activatedIntegration: null,
                clickedIntegration: null,
              });
            }
          } />
      </SlidePaneView>}
    </div>
  );
}

export default withErrorBoundary(Integrations);